/**
 * Hooks for organisation question template management
 */

import { useState, useEffect, useCallback } from 'react'
import type {
  QuestionTemplate,
  CreateQuestionTemplateRequest,
  UpdateQuestionTemplateRequest,
  ListQuestionTemplatesQuery,
} from '../types/case-container'
import {
  listQuestionTemplates,
  listCategories,
  getQuestionTemplate,
  createQuestionTemplate,
  updateQuestionTemplate,
  deleteQuestionTemplate,
  recordTemplateUsage,
} from '../services/oq-templates'
import { runMutation } from './use-collection-requests/run-mutation'

// =============================================================================
// useQuestionTemplates - List templates
// =============================================================================

interface UseQuestionTemplatesResult {
  templates: QuestionTemplate[]
  loading: boolean
  error: string | null
  refetch: (query?: ListQuestionTemplatesQuery) => Promise<void>
}

/**
 * Hook to fetch question templates for the user's organisation
 */
export function useQuestionTemplates(
  initialQuery?: ListQuestionTemplatesQuery
): UseQuestionTemplatesResult {
  const [templates, setTemplates] = useState<QuestionTemplate[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const fetch = useCallback(async (query?: ListQuestionTemplatesQuery) => {
    setLoading(true)
    setError(null)

    try {
      const data = await listQuestionTemplates(query)
      setTemplates(data)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch templates')
      setTemplates([])
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void fetch(initialQuery)
  }, [fetch, initialQuery])

  return { templates, loading, error, refetch: fetch }
}

// =============================================================================
// useQuestionTemplateCategories - List categories
// =============================================================================

interface UseQuestionTemplateCategoriesResult {
  categories: string[]
  loading: boolean
  error: string | null
  refetch: () => Promise<void>
}

/**
 * Hook to fetch available template categories
 */
export function useQuestionTemplateCategories(): UseQuestionTemplateCategoriesResult {
  const [categories, setCategories] = useState<string[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const fetch = useCallback(async () => {
    setLoading(true)
    setError(null)

    try {
      const data = await listCategories()
      setCategories(data.categories)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch categories')
      setCategories([])
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void fetch()
  }, [fetch])

  return { categories, loading, error, refetch: fetch }
}

// =============================================================================
// useQuestionTemplate - Get single template
// =============================================================================

interface UseQuestionTemplateResult {
  template: QuestionTemplate | null
  loading: boolean
  error: string | null
  refetch: () => Promise<void>
}

/**
 * Hook to fetch a single question template
 */
export function useQuestionTemplate(id: string | null): UseQuestionTemplateResult {
  const [template, setTemplate] = useState<QuestionTemplate | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const fetch = useCallback(async () => {
    if (!id) {
      setTemplate(null)
      setError(null)
      return
    }

    setLoading(true)
    setError(null)

    try {
      const data = await getQuestionTemplate(id)
      setTemplate(data)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch template')
      setTemplate(null)
    } finally {
      setLoading(false)
    }
  }, [id])

  useEffect(() => {
    void fetch()
  }, [fetch])

  return { template, loading, error, refetch: fetch }
}

// =============================================================================
// useQuestionTemplateMutations - Create, update, delete templates
// =============================================================================

interface UseQuestionTemplateMutationsResult {
  creating: boolean
  updating: boolean
  deleting: boolean
  recording: boolean
  error: string | null
  create: (input: CreateQuestionTemplateRequest) => Promise<QuestionTemplate | null>
  update: (id: string, input: UpdateQuestionTemplateRequest) => Promise<QuestionTemplate | null>
  remove: (id: string) => Promise<boolean>
  recordUsage: (id: string) => Promise<boolean>
  clearError: () => void
}

/**
 * Hook for template mutations
 */
export function useQuestionTemplateMutations(): UseQuestionTemplateMutationsResult {
  const [creating, setCreating] = useState(false)
  const [updating, setUpdating] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [recording, setRecording] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const create = useCallback(
    (input: CreateQuestionTemplateRequest) => runMutation(
      { setLoading: setCreating, setError, errorMessage: 'Failed to create template', fallback: null as QuestionTemplate | null },
      () => createQuestionTemplate(input)
    ), []
  )

  const update = useCallback(
    (id: string, input: UpdateQuestionTemplateRequest) => runMutation(
      { setLoading: setUpdating, setError, errorMessage: 'Failed to update template', fallback: null as QuestionTemplate | null },
      () => updateQuestionTemplate(id, input)
    ), []
  )

  const remove = useCallback(
    (id: string) => runMutation(
      { setLoading: setDeleting, setError, errorMessage: 'Failed to delete template', fallback: false },
      async () => { await deleteQuestionTemplate(id); return true }
    ), []
  )

  const recordUsage = useCallback(
    (id: string) => runMutation(
      { setLoading: setRecording, setError, errorMessage: 'Failed to record template usage', fallback: false },
      async () => { await recordTemplateUsage(id); return true }
    ), []
  )

  const clearError = useCallback(() => { setError(null) }, [])

  return {
    creating, updating, deleting, recording, error,
    create, update, remove, recordUsage, clearError,
  }
}
